import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, Clock, DollarSign, FolderOpen, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMyTeamProjects } from '@/hooks/useMyTeamProjects';
import { TeamProjectCard } from './TeamProjectCard';
import { EmptyState } from '@/components/EmptyState';

export function TeamMemberDashboard() {
  const navigate = useNavigate();
  const { data: myProjects = [], isLoading } = useMyTeamProjects();

  // Calculate stats for team member
  const totalProjects = myProjects.length;
  const activeProjects = myProjects.filter(p => p.status === 'active').length;
  
  if (isLoading) {
    return <div>Loading your team projects...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold">My Team Projects</h2>
        <p className="text-muted-foreground">
          Projects you're collaborating on with your teams
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeProjects}</div>
            <p className="text-xs text-muted-foreground">
              Projects you're working on
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalProjects}</div>
            <p className="text-xs text-muted-foreground">
              All projects you're part of
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Role</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Member</div>
            <p className="text-xs text-muted-foreground">
              Team collaborator
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Projects List */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Your Projects</h3>
          {myProjects.length > 0 && (
            <Button 
              variant="outline" 
              onClick={() => navigate('/team-projects')}
            >
              View All
            </Button>
          )}
        </div>

        {myProjects.length === 0 ? (
          <EmptyState
            icon={<Users className="h-12 w-12" />}
            title="No team projects yet"
            description="You haven't been added to any team projects. Ask your project manager to invite you to collaborate."
            actionLabel="Browse All Projects"
            onAction={() => navigate('/team-projects')}
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {myProjects.slice(0, 6).map((project) => (
              <TeamProjectCard
                key={project.id}
                project={project}
                onSelect={(project) => navigate(`/team-projects/${project.id}`)}
                onArchive={() => {}} // Team members can't archive
                hideArchive={project.user_role !== 'owner' && project.user_role !== 'manager'}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}